import {
  CircularProgress,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { AliasValidationStatus, validateFieldAlias } from 'api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { fieldTemplateAliases } from 'state';
import { GeneralIds } from 'types/types';

export default function AliasInput(props: TextFieldProps) {
  const [error, setError] = useState(false);
  const { t } = useTranslation();
  const { onChange: propsOnChange, ...rest } = props;
  const { projectId } = useParams<GeneralIds>();
  const [typingTimeout, setTypingTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const usedAliases = useRecoilValue(fieldTemplateAliases);
  const [errorMessage, setErrorMessage] = useState('');

  // we need to do manual valiadations to display loading state
  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    propsOnChange?.({
      target: {
        value: value,
        name: props.name ?? 'alias',
      },
    } as React.ChangeEvent<HTMLInputElement>);
    setLoading(true);
    setError(false);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // alias can't have spaces nor special characters except for underscore
    if (value.match(/[^a-zA-Z0-9_]/)) {
      setLoading(false);
      setError(true);
      setErrorMessage(t('forms.errors.aliasInvalidCharacters') ?? '');
      return;
    }

    const timeoutId = setTimeout(async () => {
      const { newAlias, status } = await validateFieldAlias({
        projectId,
        alias: value,
        usedAliases,
      });

      if (status === AliasValidationStatus.ALIAS_IS_ALREADY_USED) {
        setErrorMessage(
          t('forms.errors.aliasIsAlreadyTaken', { alias: newAlias }) ?? '',
        );
        setError(true);
      }
      setLoading(false);
    }, 500);
    setTypingTimeout(timeoutId);
  };

  return (
    <TextField
      {...rest}
      label={`${t('forms.fieldAlias')} *`}
      onChange={onChange}
      error={error}
      helperText={
        error &&
        (errorMessage || t('forms.errors.aliasIsAlreadyTaken', { alias: '' }))
      }
      inputProps={{ maxLength: 25 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {loading && <CircularProgress size={15} />}
          </InputAdornment>
        ),
      }}
    />
  );
}
